import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { Heading } from '@/components/ui/typography'
import type {
  VoicePipelineSttModelComparisonItem,
  VoicePipelineLlmModelComparisonItem,
  VoicePipelineTtsModelComparisonItem,
} from '@/lib/api/reporting.client'

type ComponentTab = 'stt' | 'llm' | 'tts'

// ── Column definitions per component ────────────────────────────────

interface ColumnDef<T> {
  key: keyof T
  label: string
  format: (v: number | string) => string
}

const STT_COLUMNS: ColumnDef<VoicePipelineSttModelComparisonItem>[] = [
  { key: 'model', label: 'Model', format: v => String(v) },
  { key: 'turnCount', label: 'Turns', format: v => Number(v).toLocaleString() },
  { key: 'callCount', label: 'Calls', format: v => Number(v).toLocaleString() },
  {
    key: 'avgSttTtft',
    label: 'Avg TTFT',
    format: v => `${Math.round(Number(v) * 1000)} ms`,
  },
  {
    key: 'avgAudioDuration',
    label: 'Avg Audio Duration',
    format: v => `${Number(v).toFixed(1)} s`,
  },
]

const LLM_COLUMNS: ColumnDef<VoicePipelineLlmModelComparisonItem>[] = [
  { key: 'model', label: 'Model', format: v => String(v) },
  { key: 'turnCount', label: 'Turns', format: v => Number(v).toLocaleString() },
  { key: 'callCount', label: 'Calls', format: v => Number(v).toLocaleString() },
  {
    key: 'latencyP50',
    label: 'Total Lat. P50',
    format: v => `${Math.round(Number(v) * 1000)} ms`,
  },
  {
    key: 'latencyP80',
    label: 'Total Lat. P80',
    format: v => `${Math.round(Number(v) * 1000)} ms`,
  },
  {
    key: 'latencyP90',
    label: 'Total Lat. P90',
    format: v => `${Math.round(Number(v) * 1000)} ms`,
  },
  {
    key: 'latencyP95',
    label: 'Total Lat. P95',
    format: v => `${Math.round(Number(v) * 1000)} ms`,
  },
  {
    key: 'latencyP99',
    label: 'Total Lat. P99',
    format: v => `${Math.round(Number(v) * 1000)} ms`,
  },
  {
    key: 'avgLlmTtft',
    label: 'Avg TTFT',
    format: v => `${Math.round(Number(v) * 1000)} ms`,
  },
  {
    key: 'avgTokensPerSecond',
    label: 'Tokens/sec',
    format: v => Number(v).toFixed(1),
  },
  {
    key: 'cacheHitRate',
    label: 'Cache Hit %',
    format: v => `${(Number(v) * 100).toFixed(1)}%`,
  },
]

const TTS_COLUMNS: ColumnDef<VoicePipelineTtsModelComparisonItem>[] = [
  { key: 'model', label: 'Model', format: v => String(v) },
  { key: 'turnCount', label: 'Turns', format: v => Number(v).toLocaleString() },
  { key: 'callCount', label: 'Calls', format: v => Number(v).toLocaleString() },
  {
    key: 'avgTtsTtfb',
    label: 'Avg TTFB',
    format: v => `${Math.round(Number(v) * 1000)} ms`,
  },
  {
    key: 'avgTtsDuration',
    label: 'Avg Duration',
    format: v => `${(Number(v) * 1000).toFixed(0)} ms`,
  },
  {
    key: 'totalTtsChars',
    label: 'Total Chars',
    format: v => Number(v).toLocaleString(),
  },
]

const TAB_CONFIG: { key: ComponentTab; label: string }[] = [
  { key: 'stt', label: 'STT' },
  { key: 'llm', label: 'LLM' },
  { key: 'tts', label: 'TTS' },
]

// ── Generic sortable table ──────────────────────────────────────────

function SortableTable<T extends { model: string }>({
  data,
  columns,
}: {
  data: T[]
  columns: ColumnDef<T>[]
}) {
  const [sortKey, setSortKey] = useState<keyof T>(
    columns[1]?.key ?? columns[0].key,
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const handleSort = (key: keyof T) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sorted = [...data].sort((a, b) => {
    const aVal = a[sortKey]
    const bVal = b[sortKey]
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal)
    }
    const diff = (aVal as number) - (bVal as number)
    return sortDir === 'asc' ? diff : -diff
  })

  const SortIcon = ({ colKey }: { colKey: keyof T }) => {
    if (sortKey !== colKey)
      return (
        <ArrowUpDown className="h-3 w-3 text-gray-400 dark:text-muted-foreground" />
      )
    return sortDir === 'asc' ? (
      <ArrowUp className="h-3 w-3 text-gray-700 dark:text-foreground" />
    ) : (
      <ArrowDown className="h-3 w-3 text-gray-700 dark:text-foreground" />
    )
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-gray-400 dark:text-muted-foreground text-center py-8">
        No model data available for the selected period
      </p>
    )
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(col => (
              <TableHead
                key={String(col.key)}
                className="cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-muted transition-colors"
                onClick={() => handleSort(col.key)}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  <SortIcon colKey={col.key} />
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((row, i) => (
            <TableRow key={String(row.model ?? i)}>
              {columns.map(col => (
                <TableCell
                  key={String(col.key)}
                  className={
                    col.key === 'model' ? 'font-medium' : 'tabular-nums'
                  }
                >
                  {col.format(row[col.key] as number | string)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────

interface ModelComparisonTableProps {
  sttData: VoicePipelineSttModelComparisonItem[]
  llmData: VoicePipelineLlmModelComparisonItem[]
  ttsData: VoicePipelineTtsModelComparisonItem[]
  isLoading: boolean
}

export function ModelComparisonTable({
  sttData,
  llmData,
  ttsData,
  isLoading,
}: ModelComparisonTableProps) {
  const [activeTab, setActiveTab] = useState<ComponentTab>('llm')

  return (
    <Card className="bg-white dark:bg-background shadow-sm border border-gray-200 dark:border-border hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <Heading variant="section-title" className="mb-1">
              Model Comparison
            </Heading>
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              Performance by actual model used per component
            </p>
          </div>
          <div className="flex rounded-lg border border-gray-200 dark:border-border p-0.5 bg-gray-50 dark:bg-muted">
            {TAB_CONFIG.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  activeTab === key
                    ? 'bg-white dark:bg-background text-gray-900 dark:text-foreground shadow-sm'
                    : 'text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            {activeTab === 'stt' && (
              <SortableTable data={sttData} columns={STT_COLUMNS} />
            )}
            {activeTab === 'llm' && (
              <SortableTable data={llmData} columns={LLM_COLUMNS} />
            )}
            {activeTab === 'tts' && (
              <SortableTable data={ttsData} columns={TTS_COLUMNS} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
